import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Title, Text, Card, Badge, Group, Stack, Button,
  Tabs, Avatar, Progress, Divider, Skeleton,
  ActionIcon, Tooltip, Loader, Center, Box,
} from '@mantine/core';
import {
  IconArrowLeft, IconUsers, IconCalendar, IconBooks, IconNotes, IconUserCheck,
  IconRefresh, IconClipboardCheck, IconMessageQuestion, IconEye, IconBabyCarriage,
} from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  Registration,
  RegistrationListView,
  RegistrationSessionModelView,
  RegistrationCourseCustomDataModelView,
} from '@actschurch/ccms-sdk';
import CourseFilesTab from './Tabs/CourseFilesTab';
import NotesTab from './Tabs/NotesTab';
import MembersTab from './Tabs/MembersTab';
import FacilitatorsTab from './Tabs/FacilitatorsTab';
import AdditionalDetailsTab from './Tabs/AdditionalDetailsTab';
import { parseChildren } from '../../Components/ChildrenTab';
import ChildrenReadOnly from './Components/ChildrenReadOnly';
import { tabStyle } from '@/components/ui/TabStyle';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { useSessionTabs } from './Hooks/useSessionTabs';
import SpecialSessionsTabContent from './SpecialSession/SpecialSessionsTabContent';
import RegistrationSessionDetailsModal from './Components/RegistrationSessionDetailsModal';
import SessionStateBadge from './Components/SessionStateBadge';
import NotFoundState from '../Components/NotFoundState';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { useCampusParam } from '@/hooks/useCampusParam';

// Static tabs keep their fixed keys; dynamic session tabs use whatever
// key buildSessionTabs() produced ('sessions', or `special-<id>`).
type StaticTabKey = 'members' | 'facilitators' | 'additionalDetails' | 'children' | 'notes' | 'courseFiles';
type TabKey = StaticTabKey | string;

/** Guard first, so an unauthorised campus URL never fires a request. */
export default function CampusRegistrationDetailsPage() {
  const { campusLabel, allowed } = useCampusParam();

  if (!allowed) {
    // swap for your existing access-denied panel
    return <Center py="xl"><Text c="red">You do not have access to {campusLabel} registrations.</Text></Center>;
  }

  return <RegistrationDetailsContent />;
}

function RegistrationDetailsContent() {
  const { id } = useParams<{ id: string }>();
  const { campus } = useCampusParam();
  const navigate = useNavigate();
  const registrationService = useRegistrationService();
  const { user } = useAuth();

  const backTo = `/Grow/CampusRegistrations/${campus}`;

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [listItem, setListItem] = useState<RegistrationListView | null>(null);
  const [childrenRow, setChildrenRow] = useState<RegistrationCourseCustomDataModelView | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const [detailsSession, setDetailsSession] = useState<RegistrationSessionModelView | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const registrationID = Number(id);

  // ── Dynamic session tabs (special sessions + one combined "Sessions" tab) ─
  const {
    tabs: sessionTabs,
    loading: sessionTabsLoading,
    refetch: refetchSessionTabs,
  } = useSessionTabs(registrationID, true); // fetched once on mount, independent of which tab is active

  // All sessions across every tab (special + normal), for the combined "Sessions" tab.
  const allSessions = useMemo(
    () => sessionTabs.flatMap((tab) => (tab.kind === 'special' ? [tab.session] : tab.sessions)),
    [sessionTabs],
  );

  const openSessionDetails = (session: RegistrationSessionModelView) => {
    setDetailsSession(session);
    setDetailsOpen(true);
  };

  // ── Fetch registration (raw record + list enrichment) ────────────────────
  const fetchRegistration = useCallback(async () => {
    if (!registrationID) return;
    setLoading(true);
    try {
      const [detailRes, listRes] = await Promise.all([
        registrationService.getById(registrationID),
        registrationService.listView(),
      ]);

      const detail: any = (detailRes as any)?.data ?? detailRes;
      setRegistration(detail ?? null);

      const items: RegistrationListView[] = (listRes as any)?.items ?? (listRes as any)?.data?.items ?? [];
      const match = items.find((r) => r.registrationID === registrationID) ?? null;
      setListItem(match);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load registration.');
    } finally {
      setLoading(false);
    }
  }, [registrationService, registrationID]);

  useEffect(() => { fetchRegistration(); }, [fetchRegistration]);

  // ── Fetch children data from course custom data ──────────────────────────
  const fetchChildrenData = useCallback(async () => {
    if (!registrationID) return;
    try {
      const res = await (registrationService as any).getCourseCustomData(registrationID);
      const data: RegistrationCourseCustomDataModelView[] = Array.isArray(res) ? res : res?.data ?? [];
      const found = data.find((r) => r.field?.fieldKey === 'children') ?? null;
      // A row with an empty or unparsable array counts as "no children" for tab visibility.
      setChildrenRow(found && parseChildren(found).length > 0 ? found : null);
    } catch (e) {
      console.error('Error fetching children data:', e);
      // Non-fatal — just hide the tab if this fails.
    }
  }, [registrationService, registrationID]);

  useEffect(() => { fetchChildrenData(); }, [fetchChildrenData]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading && !registration) {
    return (
      <Container size="xl" py="xl">
        <Skeleton height={36} width={220} mb="md" />
        <Skeleton height={140} radius="md" mb="md" />
        <Skeleton height={400} radius="md" />
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container size="xl" py="xl">
        <NotFoundState entityLabel="registration" backTo={backTo} />
      </Container>
    );
  }

  const statusColor = listItem?.effectiveStatusColor ?? listItem?.statusColor ?? undefined;
  const statusName  = listItem?.effectiveStatusName  ?? listItem?.statusName  ?? '—';
  const courseName  = listItem?.courseName ?? `Course ${registration.courseID}`;

  return (
    <Container size="xl" py="xl">
      {/* ── Back navigation ── */}
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(backTo)}
        mb="md"
      >
        Back to Registrations
      </Button>

      {/* ── Header row: Title + Refresh ── */}
      <Group justify="space-between" mb="sm">
        <Title order={2}>Registration Details</Title>
        <Tooltip label="Refresh">
          <ActionIcon
            variant="subtle"
            onClick={() => { fetchRegistration(); refetchSessionTabs(); fetchChildrenData(); }}
            loading={loading}
            size="lg"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group align="flex-start" gap="md">
            <Avatar color={listItem?.courseColor ?? 'blue'} radius="md" size={80}>
              <IconBooks size={28} />
            </Avatar>

            <div>
              <Group gap="xs" align="center">
                <Title order={4}>{courseName}</Title>
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(statusColor)}>
                  {statusName}
                </Badge>
                {listItem?.locationName && (
                  <Badge
                    variant="filled"
                    styles={badgeStyles}
                    style={brandBadgeStyle(listItem.locationColor)}
                  >
                    {listItem.locationName}
                  </Badge>
                )}
              </Group>
              {registration.description && (
                <Text size="sm" c="dimmed">{registration.description}</Text>
              )}
              <Text size="sm" c="dimmed">
                {listItem?.periodDisplay}
              </Text>
              {/* ⚠️ Read off `listItem` (the RegistrationListView row), not `registration`. The page
                  holds the **entity** from `getById`, which carries neither of these; the list row
                  carries both. */}
              {listItem?.registeredDate && (
                <Text size="xs" c="dimmed">Registered {listItem.registeredDate}</Text>
              )}
            </div>
          </Group>

          <Group gap="xl">
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Participants</Text>
              <Group gap={4}>
                <IconUsers size={16} />
                <Text fw={600}>{listItem?.participantCount ?? 0}</Text>
              </Group>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Lead Facilitator</Text>
              <Text fw={600}>{listItem?.leadFacilitatorName ?? '—'}</Text>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Next Session</Text>
              <Text fw={600}>{listItem?.nextSessionDisplay ?? '—'}</Text>
            </Stack>
          </Group>
        </Group>

        {typeof listItem?.progressPercent === 'number' && (
          <>
            <Divider my="sm" />
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">Progress</Text>
              <Text size="xs" c="dimmed">{listItem.progressPercent}%</Text>
            </Group>
            <Progress
              value={listItem.progressPercent}
              color={listItem.isBehindSchedule ? 'red' : 'blue'}
              radius="xl"
            />
          </>
        )}
      </Card>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={(v) => setActiveTab((v as TabKey) ?? 'members')} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="members" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'members')}>Members</Tabs.Tab>
          <Tabs.Tab value="facilitators" leftSection={<IconUserCheck size={16} />} style={tabStyle(activeTab === 'facilitators')}>Facilitators</Tabs.Tab>

          {/* ── Dynamic session tabs: one per special session, plus one combined "Sessions" tab ── */}
          {sessionTabsLoading && sessionTabs.length === 0 && (
            <Center px="md">
              <Loader size="xs" />
            </Center>
          )}
          {sessionTabs.map((tab) => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              leftSection={tab.kind === 'special' ? <IconClipboardCheck size={16} /> : <IconCalendar size={16} />}
              style={tabStyle(activeTab === tab.key)}
            >
              {tab.title}
            </Tabs.Tab>
          ))}

          <Tabs.Tab value="additionalDetails" leftSection={<IconMessageQuestion size={16} />} style={tabStyle(activeTab === 'additionalDetails')}>Additional Details</Tabs.Tab>
          {childrenRow && (
            <Tabs.Tab value="children" leftSection={<IconBabyCarriage size={16} />} style={tabStyle(activeTab === 'children')}>
              Children
            </Tabs.Tab>
          )}
          <Tabs.Tab value="notes" leftSection={<IconNotes size={16} />} style={tabStyle(activeTab === 'notes')}>Notes</Tabs.Tab>
          <Tabs.Tab value="courseFiles" leftSection={<IconBooks size={16} />} style={tabStyle(activeTab === 'courseFiles')}>Course Files</Tabs.Tab>
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">
          <Tabs.Panel value="members">
            <MembersTab registrationId={registrationID} active={activeTab === 'members'} />
          </Tabs.Panel>
          <Tabs.Panel value="facilitators">
            <FacilitatorsTab registrationId={registrationID} active={activeTab === 'facilitators'} />
          </Tabs.Panel>

          {/* ── Dynamic session tab panels ── */}
          {sessionTabs.map((tab) => (
            <Tabs.Panel key={tab.key} value={tab.key}>
              {tab.kind === 'special' ? (
                <SpecialSessionsTabContent
                  registrationID={registrationID}
                  registrationSessionID={tab.session.registrationSessionID}
                />
              ) : (
                <Stack gap="md">
                  <Group justify="flex-end" gap={4}>
                    <Tooltip label="Refresh sessions" withArrow>
                      <ActionIcon
                        variant="subtle"
                        onClick={refetchSessionTabs}
                        loading={sessionTabsLoading}
                        size="lg"
                      >
                        <IconRefresh size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Stack gap={0}>
                    <Group justify="space-between" px="sm" pb={6} align="center">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" c="dimmed" fw={500} tt="uppercase">Session</Text>
                      </Box>
                      <Group gap="xs" align="center">
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 130, textAlign: 'left' }}>Date</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Progress</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Status</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Actions</Text>
                      </Group>
                    </Group>
                    <Divider mb={4} />
                    {allSessions.map((s) => (
                      <Group
                        key={s.registrationSessionID}
                        justify="space-between"
                        align="flex-start"
                        py="sm"
                        px="sm"
                        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                      >
                        <Stack gap={2}>
                          <Group gap={6}>
                            <Text size="sm" fw={500}>{s.name ?? '—'}</Text>
                            {s.isSpecial && (
                              <Badge size="xs" variant="light" color="grape">Special</Badge>
                            )}
                            {s.isToday && (
                              <Badge size="xs" variant="filled" color="blue">Today</Badge>
                            )}
                            {s.isOverdue && (
                              <Badge size="xs" variant="filled" color="red">Overdue</Badge>
                            )}
                          </Group>
                          {s.description && (
                            <Text size="xs" c="dimmed" lineClamp={1}>{s.description}</Text>
                          )}
                          {s.templateName && s.templateName !== s.name && (
                            <Text size="xs" c="dimmed">Template: {s.templateName}</Text>
                          )}
                        </Stack>

                        <Group gap="xs" align="center">
                          <Group gap={4} style={{ minWidth: 130 }}>
                            <IconCalendar size={14} style={{ opacity: 0.6 }} />
                            <Text size="xs" c="dimmed">{s.sessionDateDisplay ?? '—'}</Text>
                          </Group>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateBadge session={s} kind="progress" />
                          </Box>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateBadge session={s} kind="status" />
                          </Box>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <Group gap={4} justify="center" wrap="nowrap">
                              <Tooltip label="View session">
                                <ActionIcon
                                  variant="light"
                                  color="blue"
                                  onClick={() => openSessionDetails(s)}
                                >
                                  <IconEye size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Box>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Tabs.Panel>
          ))}

          <Tabs.Panel value="additionalDetails">
            <AdditionalDetailsTab registrationId={registrationID} active={activeTab === 'additionalDetails'} />
          </Tabs.Panel>
          {childrenRow && (
            <Tabs.Panel value="children">
              <ChildrenReadOnly row={childrenRow} onRefresh={fetchChildrenData} />
            </Tabs.Panel>
          )}
          <Tabs.Panel value="notes">
            <NotesTab
              registrationId={registrationID}
              active={activeTab === 'notes'}
              currentUserId={user?.id ?? undefined}
            />
          </Tabs.Panel>
          <Tabs.Panel value="courseFiles">
            <CourseFilesTab
              registrationId={registrationID}
              active={activeTab === 'courseFiles'}
              onRegistrationChange={fetchRegistration}
            />
          </Tabs.Panel>
        </Card>
      </Tabs>

      {/* ── Session Details Modal (view + session notes) ── */}
      {detailsSession && (
        <RegistrationSessionDetailsModal
          opened={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            setDetailsSession(null);
          }}
          session={detailsSession}
          currentUserId={user?.id ?? undefined}
        />
      )}
    </Container>
  );
}
